import { createQuery } from '@shared/lib/query-helpers';

import {
  certificationRegistrationProgressResponseSchema,
  personalFlowFeedResponseSchema,
  personalFlowProgramsResponseSchema,
  personalFlowScopedItemsResponseSchema,
  trainingProgramProgressResponseSchema,
} from './schemas';

export const personalFlowKeys = {
  all: ['personal-flow'] as const,
  actor: (accountId: string) => [...personalFlowKeys.all, { accountId }] as const,
  feed: (accountId: string, slidesCount: number) =>
    [...personalFlowKeys.actor(accountId), 'feed', { slidesCount }] as const,
  profile: (accountId: string, profileRef: string) =>
    [...personalFlowKeys.actor(accountId), { profileRef }] as const,
  certificationRegistration: (
    accountId: string,
    profileRef: string,
    registrationRef: string,
    slidesCount: number
  ) =>
    [
      ...personalFlowKeys.profile(accountId, profileRef),
      'certification-registration',
      { registrationRef, slidesCount },
    ] as const,
  trainingProgram: (
    accountId: string,
    profileRef: string,
    programRef: string,
    slidesCount: number
  ) =>
    [
      ...personalFlowKeys.profile(accountId, profileRef),
      'training-program',
      { programRef, slidesCount },
    ] as const,
  certificationRegistrationProgress: (
    accountId: string,
    profileRef: string,
    registrationRef: string
  ) =>
    [
      ...personalFlowKeys.profile(accountId, profileRef),
      'certification-registration-progress',
      { registrationRef },
    ] as const,
  trainingProgramProgress: (accountId: string, profileRef: string, programRef: string) =>
    [
      ...personalFlowKeys.profile(accountId, profileRef),
      'training-program-progress',
      { programRef },
    ] as const,
  general: (accountId: string, profileRef: string, slidesCount: number) =>
    [...personalFlowKeys.profile(accountId, profileRef), 'general', { slidesCount }] as const,
  programs: (accountId: string, profileRef: string) =>
    [...personalFlowKeys.profile(accountId, profileRef), 'settings', 'programs'] as const,
};

export const certificationRegistrationProgressQuery = (input: {
  accountId: string;
  profileRef: string;
  registrationRef: string;
  enabled?: boolean;
}) =>
  createQuery({
    queryKey: personalFlowKeys.certificationRegistrationProgress(
      input.accountId,
      input.profileRef,
      input.registrationRef
    ),
    path: `/api/v1/user-profiles/${encodeURIComponent(input.profileRef)}/registrations/${encodeURIComponent(input.registrationRef)}/progress`,
    schema: certificationRegistrationProgressResponseSchema,
    staleTime: 60_000,
    gcTime: 10 * 60_000,
    enabled:
      (input.enabled ?? true) &&
      input.accountId.length > 0 &&
      input.profileRef.length > 0 &&
      input.registrationRef.length > 0,
  });

export const trainingProgramProgressQuery = (input: {
  accountId: string;
  profileRef: string;
  programRef: string;
  enabled?: boolean;
}) =>
  createQuery({
    queryKey: personalFlowKeys.trainingProgramProgress(
      input.accountId,
      input.profileRef,
      input.programRef
    ),
    path: `/api/v1/user-profiles/${encodeURIComponent(input.profileRef)}/training-programs/${encodeURIComponent(input.programRef)}/progress`,
    schema: trainingProgramProgressResponseSchema,
    staleTime: 60_000,
    gcTime: 10 * 60_000,
    enabled:
      (input.enabled ?? true) &&
      input.accountId.length > 0 &&
      input.profileRef.length > 0 &&
      input.programRef.length > 0,
  });

export const personalFlowFeedQuery = (accountId: string, slidesCount = 40) =>
  createQuery({
    queryKey: personalFlowKeys.feed(accountId, slidesCount),
    path: '/api/v1/user-profiles/me/personal-flow/items',
    schema: personalFlowFeedResponseSchema,
    requestOptions: {
      params: { slides_count: slidesCount, return_data: 'true' },
    },
    staleTime: 60_000,
    gcTime: 10 * 60_000,
    enabled: accountId.length > 0,
    // Personal Flow is an idempotent actor-keyed read. Keeping the request alive lets the
    // development StrictMode remount reuse the same in-flight projection instead of starting a
    // second expensive PostgreSQL composition.
    consumeAbortSignal: false,
  });

export const personalFlowCertificationRegistrationQuery = (input: {
  accountId: string;
  profileRef: string;
  registrationRef: string;
  slidesCount?: number;
  enabled?: boolean;
}) => {
  const slidesCount = input.slidesCount ?? 40;
  return createQuery({
    queryKey: personalFlowKeys.certificationRegistration(
      input.accountId,
      input.profileRef,
      input.registrationRef,
      slidesCount
    ),
    path: `/api/v1/user-profiles/${encodeURIComponent(input.profileRef)}/personal-flow/certification-registrations/${encodeURIComponent(input.registrationRef)}/items`,
    schema: personalFlowScopedItemsResponseSchema,
    requestOptions: { params: { slides_count: slidesCount } },
    staleTime: 60_000,
    gcTime: 10 * 60_000,
    enabled:
      (input.enabled ?? true) &&
      input.accountId.length > 0 &&
      input.profileRef.length > 0 &&
      input.registrationRef.length > 0,
  });
};

export const personalFlowTrainingProgramQuery = (input: {
  accountId: string;
  profileRef: string;
  programRef: string;
  slidesCount?: number;
  enabled?: boolean;
}) => {
  const slidesCount = input.slidesCount ?? 40;
  return createQuery({
    queryKey: personalFlowKeys.trainingProgram(
      input.accountId,
      input.profileRef,
      input.programRef,
      slidesCount
    ),
    path: `/api/v1/user-profiles/${encodeURIComponent(input.profileRef)}/personal-flow/training-programs/${encodeURIComponent(input.programRef)}/items`,
    schema: personalFlowScopedItemsResponseSchema,
    requestOptions: { params: { slides_count: slidesCount } },
    staleTime: 60_000,
    gcTime: 10 * 60_000,
    enabled:
      (input.enabled ?? true) &&
      input.accountId.length > 0 &&
      input.profileRef.length > 0 &&
      input.programRef.length > 0,
  });
};

export const personalFlowGeneralQuery = (input: {
  accountId: string;
  profileRef: string;
  slidesCount?: number;
  enabled?: boolean;
}) => {
  const slidesCount = input.slidesCount ?? 40;
  return createQuery({
    queryKey: personalFlowKeys.general(input.accountId, input.profileRef, slidesCount),
    path: `/api/v1/user-profiles/${encodeURIComponent(input.profileRef)}/personal-flow/general/items`,
    schema: personalFlowScopedItemsResponseSchema,
    requestOptions: { params: { slides_count: slidesCount } },
    staleTime: 60_000,
    gcTime: 10 * 60_000,
    enabled: (input.enabled ?? true) && input.accountId.length > 0 && input.profileRef.length > 0,
  });
};

export const personalFlowProgramsQuery = (accountId: string, profileRef: string, enabled = true) =>
  createQuery({
    queryKey: personalFlowKeys.programs(accountId, profileRef),
    path: `/api/v1/user-profiles/${encodeURIComponent(profileRef)}/training-programs`,
    schema: personalFlowProgramsResponseSchema,
    staleTime: 60_000,
    enabled: enabled && accountId.length > 0 && profileRef.length > 0,
  });
